<script setup lang="ts">
import { watch } from "vue";
import { ElMessageBox } from "element-plus";
import { useRouter } from "vue-router";
import { useLeadsStore } from "~/projects/crm/stores/leadsStore";
import TableContainer from "~/components/TableContainer.vue";
import Helper from "~/composable/helper";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue", "completed"]);

const leadsStore = useLeadsStore();
const router = useRouter();
const { toTitleCase } = Helper();

const fetchFollowUps = async () => {
  await leadsStore.getTodayFollowUps();
};

// The list is already scoped to the logged-in agent, so completing only needs
// the follow-up to still be open.
const canComplete = (row: any) =>
  row.formatted_status === "pending" || row.formatted_status === "failed";

const completeTask = async (data: any) => {
  // The store refetches today's list after a successful close, so the row and
  // the badge both drop without a second call from here.
  await leadsStore.closeFollowUpTask({ id: data.id });
  emit("completed");
};

const confirmCompleteTask = (leadFollowUp: any) => {
  ElMessageBox.confirm("Do you want to complete the task?", "Warning", {
    confirmButtonText: "OK",
    cancelButtonText: "Cancel",
    type: "warning",
  })
    .then(async () => {
      await completeTask(leadFollowUp);
    })
    .catch(() => {});
};

const close = () => emit("update:modelValue", false);

const goToLead = (row: any) => {
  close();
  router.push(`/crm/leads/${row.lead_id}`);
};

// Refetch on every open so the table never shows a stale snapshot from the
// last time the modal was used.
watch(
  () => props.modelValue,
  async (open) => {
    if (open) await fetchFollowUps();
  },
);
</script>

<template>
  <el-dialog
    :model-value="modelValue"
    width="80%"
    align-center
    destroy-on-close
    @update:model-value="(val) => emit('update:modelValue', val)"
  >
    <template #header="{ titleId, titleClass }">
      <div class="my-header flex flex-col gap-1">
        <h4 :id="titleId" class="text-md" :class="titleClass">
          Today's Followups ({{ leadsStore.todayFollowUpsCount }})
        </h4>
      </div>
    </template>

    <TableContainer
      :table-data="leadsStore.todayFollowUps"
      :loading="leadsStore.todayFollowUpsLoading"
      max-height="60vh"
    >
      <el-table-column label="Lead ID" prop="lead_id" width="100">
        <template #default="scope">
          <!-- A real link so middle-click / open-in-new-tab still work; the
               click handler only closes the dialog behind the navigation. -->
          <NuxtLink
            v-if="scope.row.lead_id"
            :to="`/crm/leads/${scope.row.lead_id}`"
            class="text-purple-700 hover:underline font-medium"
            @click="close"
          >
            {{ scope.row.lead_id }}
          </NuxtLink>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="Lead Title" prop="lead_title">
        <template #default="scope">
          {{ scope.row.lead_title }}
        </template>
      </el-table-column>
      <el-table-column label="Followup Title" prop="title">
        <template #default="scope">
          {{ scope.row.title }}
        </template>
      </el-table-column>
      <el-table-column label="Category" prop="category_name">
        <template #default="scope">
          <el-tag v-if="scope.row.category_name" type="warning" effect="dark">{{
            scope.row.category_name
          }}</el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="Follow Up At" prop="follow_up_at">
        <template #default="scope">
          {{ formatDateTime(scope.row.follow_up_at) }}
        </template>
      </el-table-column>
      <el-table-column label="Status" prop="formatted_status" width="110">
        <template #default="scope">
          <el-tag
            :type="scope.row.formatted_status === 'failed' ? 'danger' : ''"
            >{{ toTitleCase(scope.row.formatted_status) }}</el-tag
          >
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="170">
        <template #default="scope">
          <div class="flex items-center gap-2 h-full">
            <NavButton
              v-if="canComplete(scope.row)"
              class="text-blue-500"
              @click="confirmCompleteTask(scope.row)"
            >
              Complete
            </NavButton>
            <NavButton type="text" @click="goToLead(scope.row)">View</NavButton>
          </div>
        </template>
      </el-table-column>
    </TableContainer>
  </el-dialog>
</template>
